import { fmtFull, fmtMonthDayYear } from '@concord/domain';
import { Drawer, Notice } from '@concord/ui';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api, errorMessage } from '../../lib/api';

type TermsKind = 'terms_of_use' | 'engagement';
interface TermsDoc {
  kind: TermsKind;
  version: string;
  publishedAt: string;
  body: string;
}

const TITLE: Record<TermsKind, string> = {
  terms_of_use: 'Terms of use',
  engagement: 'Consultation engagement terms',
};

/**
 * A version stamp that opens the exact text of that version: what the patient agreed to at
 * submission, or what a physician acknowledged when accepting. Opening it is audited.
 */
export function TermsLink({
  kind,
  version,
  who,
  acceptedAt,
  tz,
  label,
}: {
  kind: TermsKind;
  version: string;
  /** Who accepted, for the drawer's line ("Walter Brennan", "Dr. Thomas Nwosu"). */
  who: string;
  acceptedAt: string | null;
  tz: string;
  /** Link words; defaults to "v<version>". */
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  const q = useQuery({
    queryKey: ['terms', kind, version],
    queryFn: () => api<TermsDoc>(`/staff/terms/${kind}/${encodeURIComponent(version)}`),
    enabled: open,
    staleTime: Number.POSITIVE_INFINITY,
  });
  return (
    <>
      <button
        type="button"
        className="link"
        onClick={() => setOpen(true)}
        aria-label={`Read ${TITLE[kind].toLowerCase()} version ${version}`}
      >
        {label ?? `v${version}`}
      </button>
      <Drawer
        open={open}
        onOpenChange={setOpen}
        title={`${TITLE[kind]} · v${version}`}
        description={
          q.data
            ? `Published ${fmtMonthDayYear(new Date(q.data.publishedAt), tz)}.${
                acceptedAt ? ` Accepted by ${who} ${fmtFull(new Date(acceptedAt), tz)}.` : ''
              } This is the exact text of that version.`
            : undefined
        }
      >
        {q.isError && <Notice tone="bad">{errorMessage(q.error)}</Notice>}
        {q.data && (
          <div style={{ whiteSpace: 'pre-wrap', fontSize: '.92rem', lineHeight: 1.6, color: 'var(--ink)' }}>
            {q.data.body}
          </div>
        )}
      </Drawer>
    </>
  );
}
